import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, RefreshCw, Copy, ExternalLink } from 'lucide-react';
import { validateEnvironment, getValidationReport, getEnvironmentConfig } from '../../lib/envValidation';

interface EnvironmentValidatorProps {
  showDetails?: boolean;
  showWarnings?: boolean;
}

export default function EnvironmentValidator({ showDetails = false, showWarnings = true }: EnvironmentValidatorProps) {
  const [validation, setValidation] = useState(validateEnvironment());
  const [isExpanded, setIsExpanded] = useState(showDetails);
  const [copied, setCopied] = useState<string | null>(null);

  const refreshValidation = () => {
    setValidation(validateEnvironment());
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const openEnvFile = () => {
    // In development, try to open the .env.local file
    if (import.meta.env.DEV) {
      console.log('To edit environment variables, open .env.local in your editor');
    }
  };

  useEffect(() => {
    // Auto-refresh validation every 30 seconds in development
    if (import.meta.env.DEV) {
      const interval = setInterval(refreshValidation, 30000);
      return () => clearInterval(interval);
    }
  }, []);

  if (!import.meta.env.DEV) {
    return null; // Only show in development
  }

  const getStatusIcon = () => {
    if (!validation.isValid) return <XCircle className="w-5 h-5 text-red-500" />;
    if (validation.warnings.length > 0) return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    return <CheckCircle className="w-5 h-5 text-green-500" />;
  };

  const getStatusText = () => {
    if (!validation.isValid) return 'Environment Invalid';
    if (validation.warnings.length > 0) return 'Environment Valid (Warnings)';
    return 'Environment Valid';
  };

  const getStatusColor = () => {
    if (!validation.isValid) return 'text-red-600 bg-red-50 border-red-200';
    if (validation.warnings.length > 0) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {getStatusIcon()}
              <span className="font-medium text-gray-900">Environment Status</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={refreshValidation}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Refresh validation"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                <Info className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Status Badge */}
          <div className={`mt-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor()}`}>
            {getStatusText()}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="px-4 py-3 space-y-3">
            {/* Errors */}
            {validation.errors.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-red-700 flex items-center space-x-1">
                  <XCircle className="w-4 h-4" />
                  <span>Errors ({validation.errors.length})</span>
                </h4>
                <div className="space-y-1">
                  {validation.errors.map((error, index) => (
                    <div key={index} className="text-xs text-red-600 bg-red-50 p-2 rounded">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warnings */}
            {showWarnings && validation.warnings.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-yellow-700 flex items-center space-x-1">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Warnings ({validation.warnings.length})</span>
                </h4>
                <div className="space-y-1">
                  {validation.warnings.map((warning, index) => (
                    <div key={index} className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                      {warning}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="pt-2 border-t border-gray-200">
              <div className="flex space-x-2">
                <button
                  onClick={() => copyToClipboard(getValidationReport(), 'validation report')}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                >
                  <Copy className="w-3 h-3" />
                  <span>{copied === 'validation report' ? 'Copied!' : 'Copy Report'}</span>
                </button>
                <button
                  onClick={openEnvFile}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  <span>Edit .env</span>
                </button>
              </div>
            </div>

            {/* Environment Info */}
            <div className="pt-2 border-t border-gray-200">
              <div className="text-xs text-gray-500 space-y-1">
                <div>Mode: {import.meta.env.MODE}</div>
                <div>Node: {import.meta.env.NODE_ENV}</div>
                <div>Dev: {import.meta.env.DEV ? 'Yes' : 'No'}</div>
                <div>Prod: {import.meta.env.PROD ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
